import type { CommercialCapabilityPage } from "@bw/shared/types/generated";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { useCommercialCapabilities } from "@/app/useCommercialCapabilities";

export interface CommercialCapabilityFactsProps {
  page: CommercialCapabilityPage;
  onRetry: () => void;
}

/** Advisory denied/unknown facts, never licence/RBAC/readiness as permission. */
export function CommercialCapabilityFacts({
  page,
  onRetry,
}: CommercialCapabilityFactsProps) {
  const labels: Record<string, string> = {
    new_authoring: "New authoring",
    new_sales: "New sales",
    financial_evidence: "Financial evidence",
    history: "History",
    refund: "Refund",
    billing_cancellation: "Billing cancellation",
    billing_recovery: "Billing recovery",
    privacy_export: "Privacy export",
    privacy_deletion: "Privacy deletion",
    safety: "Safety",
    existing_fulfillment: "Existing fulfillment",
  };
  return (
    <Stack spacing={2}>
      {page.facts.map((fact, index) => (
        <Stack key={index} spacing={0.25}>
          <Typography variant="subtitle2">
            {labels[fact.operation] ?? "Workspace operation"}
          </Typography>
          <Typography variant="body2">
            {fact.decision === "denied"
              ? "Denied"
              : fact.decision === "allowed"
                ? "Current scope fact"
                : "Unknown — unresolved"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {fact.reason_codes.includes("commercial_allocation_unresolved")
              ? "Commercial allocation is unresolved. Ask the organization owner for review."
              : "The operational workspace must check its current authorization."}
          </Typography>
        </Stack>
      ))}
      <Typography variant="body2" color="text.secondary">
        These facts do not grant a licence, finance rights or permission to
        perform an operation.
      </Typography>
      <Button size="small" variant="outlined" onClick={onRetry}>
        Refresh current access
      </Button>
    </Stack>
  );
}

/** Staff-setup + verified facility scope; no Team-management or finance gate. */
export function ScopedCapabilitiesPage() {
  const org = useMyOrg();
  const { facilityId, isLoading } = useResolvedFacility();
  const query = useCommercialCapabilities(org.org?.org_id, facilityId);
  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 } }}>
      <Typography component="h1" variant="h4">
        Workspace access
      </Typography>
      {org.kind === "loading" || isLoading || query.isLoading ? (
        <Typography role="status">Checking current access…</Typography>
      ) : query.isError ? (
        <Alert severity="warning">
          Current access unavailable.{" "}
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : !facilityId ? (
        <Typography>Select a facility to check its current scope.</Typography>
      ) : query.data ? (
        <CommercialCapabilityFacts
          page={query.data}
          onRetry={() => void query.refetch()}
        />
      ) : null}
    </Stack>
  );
}
